/**
 * debugLog.ts
 *
 * Intercepts console output into a capped in-memory ring buffer for bug reports,
 * while also mirroring native Android logs to logcat in debuggable builds.
 * Provides uploadDebugLogs() to POST the current report payload to /api/upload_log.
 *
 * Install early with installDebugLogCapture().
 */

import { appDisplayVersion } from './appVersion';
import { shouldCaptureConsoleEntryForReport } from './debugLogFilter';
import { mirrorConsoleToNative } from './nativeDebugBridge';

const MAX_ENTRIES = 5000;
const STORAGE_KEY = 'wereader.debugLogs.v1';
const MAX_LOG_DEPTH = 2;
const MAX_LOG_KEYS = 20;
const MAX_LOG_ITEMS = 20;
const MAX_LOG_STRING = 300;
const MAX_LOG_MESSAGE = 4000;

interface LogEntry {
  t: string;      // ISO timestamp
  lvl: string;    // 'debug' | 'warn' | 'info' | 'error'
  msg: string;    // stringified args
}

interface BugReportDocumentSnapshot {
  kind: 'top-document' | 'iframe-document';
  index?: number;
  frameId?: string;
  frameName?: string;
  frameClassName?: string;
  src?: string;
  currentSrc?: string;
  title?: string;
  url?: string;
  visible?: boolean;
  rect?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  html?: string | null;
  error?: string;
}

interface BugReportDomSnapshot {
  capturedAt: string;
  location: string;
  title: string;
  viewport: {
    innerWidth: number;
    innerHeight: number;
    scrollX: number;
    scrollY: number;
    devicePixelRatio: number;
  };
  documents: BugReportDocumentSnapshot[];
}

interface BugReportUploadPayload {
  version: string;
  entryPoint?: string;
  description?: string;
  logs: LogEntry[];
  domSnapshot: BugReportDomSnapshot;
}

const PUBLIC_FALLBACK_ENTRY_POINTS = new Set(['react-error-boundary']);

const buffer: LogEntry[] = [];
let persistTimer: number | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error('[debug-log] listener failed', error);
    }
  });
}

function persistBuffer() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch (error) {
    try {
      // If storage is full, aggressively trim the oldest half and retry once.
      if (buffer.length > 1) {
        buffer.splice(0, Math.ceil(buffer.length / 2));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
      }
    } catch {
      console.warn('[debug-log] failed to persist logs', error);
    }
  }
}

function flushPersistNow() {
  if (persistTimer !== null) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  persistBuffer();
}

function schedulePersist() {
  if (persistTimer !== null) {
    return;
  }
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    persistBuffer();
  }, 250);
}

function hydrateBuffer() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return;
    }
    buffer.splice(0, buffer.length, ...parsed.slice(-MAX_ENTRIES).filter((entry) => entry && typeof entry.msg === 'string'));
    notifyListeners();
  } catch (error) {
    console.warn('[debug-log] failed to restore persisted logs', error);
  }
}

export { shouldCaptureConsoleEntryForReport } from './debugLogFilter';

function summarizeLogValue(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): unknown {
  if (typeof value === 'string') {
    return value.length > MAX_LOG_STRING
      ? `${value.slice(0, MAX_LOG_STRING)}... [${value.length} chars]`
      : value;
  }

  if (value === null || value === undefined || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (seen.has(value)) {
    return '[Circular]';
  }
  seen.add(value);

  if (depth >= MAX_LOG_DEPTH) {
    return Array.isArray(value)
      ? `[Array(${value.length})]`
      : `[Object(${Object.keys(value).length})]`;
  }

  if (Array.isArray(value)) {
    const items = value
      .slice(0, MAX_LOG_ITEMS)
      .map((item) => summarizeLogValue(item, depth + 1, seen));
    if (value.length > MAX_LOG_ITEMS) {
      items.push(`... ${value.length - MAX_LOG_ITEMS} more items`);
    }
    return items;
  }

  const entries = Object.entries(value);
  const summarized: Record<string, unknown> = {};
  entries.slice(0, MAX_LOG_KEYS).forEach(([key, entryValue]) => {
    summarized[key] = summarizeLogValue(entryValue, depth + 1, seen);
  });
  if (entries.length > MAX_LOG_KEYS) {
    summarized.__truncatedKeys = entries.length - MAX_LOG_KEYS;
  }
  return summarized;
}

function stringifyLogArg(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';

  try {
    const serialized =
      typeof value === 'object'
        ? JSON.stringify(summarizeLogValue(value))
        : String(value);
    return serialized.length > MAX_LOG_MESSAGE
      ? `${serialized.slice(0, MAX_LOG_MESSAGE)}... [truncated ${serialized.length - MAX_LOG_MESSAGE} chars]`
      : serialized;
  } catch {
    return String(value);
  }
}

function push(lvl: string, args: unknown[], flushImmediately = false) {
  const entry: LogEntry = {
    t: new Date().toISOString(),
    lvl,
    msg: args.map((a) => stringifyLogArg(a)).join(' '),
  };
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) {
    buffer.splice(0, buffer.length - MAX_ENTRIES);
  }
  notifyListeners();
  if (flushImmediately) {
    flushPersistNow();
  } else {
    schedulePersist();
  }
}

let installed = false;

export function installDebugLogCapture() {
  if (installed) return;
  installed = true;
  hydrateBuffer();

  const flushOnUnload = () => {
    flushPersistNow();
  };
  window.addEventListener('pagehide', flushOnUnload);
  window.addEventListener('beforeunload', flushOnUnload);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushPersistNow();
    }
  });

  const originalDebug = console.debug.bind(console);
  const originalLog = console.log.bind(console);
  const originalWarn = console.warn.bind(console);
  const originalInfo = console.info.bind(console);
  const originalError = console.error.bind(console);

  console.log = (...args: unknown[]) => {
    mirrorConsoleToNative('log', args);
    originalLog(...args);
  };

  console.debug = (...args: unknown[]) => {
    const shouldCapture = shouldCaptureConsoleEntryForReport('debug', args);
    if (shouldCapture) {
      push('debug', args, true);
    }
    if (import.meta.env.DEV || shouldCapture) {
      mirrorConsoleToNative('debug', args);
      originalDebug(...args);
    }
  };

  console.warn = (...args: unknown[]) => {
    if (shouldCaptureConsoleEntryForReport('warn', args)) push('warn', args, true);
    mirrorConsoleToNative('warn', args);
    originalWarn(...args);
  };

  console.info = (...args: unknown[]) => {
    if (shouldCaptureConsoleEntryForReport('info', args)) push('info', args, true);
    mirrorConsoleToNative('info', args);
    originalInfo(...args);
  };

  console.error = (...args: unknown[]) => {
    if (shouldCaptureConsoleEntryForReport('error', args)) push('error', args, true);
    mirrorConsoleToNative('error', args);
    originalError(...args);
  };
}

export function getDebugLogs(): LogEntry[] {
  return [...buffer];
}

export function getDebugLogCount(): number {
  return buffer.length;
}

export function subscribeDebugLogs(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function clearDebugLogs() {
  buffer.splice(0, buffer.length);
  if (persistTimer !== null) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
  notifyListeners();
}

function prependReportMetadata(logs: LogEntry[], description?: string): LogEntry[] {
  const trimmedDescription = description?.trim();
  const headerLines = ['[bug-report]', `version: v${appDisplayVersion}`];

  if (trimmedDescription) {
    headerLines.push('', trimmedDescription);
  }

  return [
    {
      t: new Date().toISOString(),
      lvl: 'info',
      msg: headerLines.join('\n'),
    },
    ...logs,
  ];
}

function captureRenderedDomSnapshot(): BugReportDomSnapshot {
  const collectDocumentSnapshot = (
    doc: Document,
    base: Omit<BugReportDocumentSnapshot, 'html' | 'error'>,
  ): BugReportDocumentSnapshot => {
    try {
      return {
        ...base,
        title: doc.title,
        url: doc.location?.href,
        html: doc.documentElement?.outerHTML ?? null,
      };
    } catch (error) {
      return {
        ...base,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  };

  const iframeSnapshots = Array.from(document.querySelectorAll('iframe')).map(
    (frame, index): BugReportDocumentSnapshot => {
      const rect = frame.getBoundingClientRect();
      const style = window.getComputedStyle(frame);
      const visible =
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden';

      const base: Omit<BugReportDocumentSnapshot, 'html' | 'error'> = {
        kind: 'iframe-document',
        index,
        frameId: frame.id || undefined,
        frameName: frame.getAttribute('name') || undefined,
        frameClassName: frame.className || undefined,
        src: frame.getAttribute('src') || undefined,
        currentSrc: frame.src || undefined,
        visible,
        rect: {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        },
      };

      const frameDoc = frame.contentDocument;
      if (!frameDoc) {
        return {
          ...base,
          error: 'Iframe document was not available during bug report capture.',
        };
      }

      return collectDocumentSnapshot(frameDoc, base);
    },
  );

  return {
    capturedAt: new Date().toISOString(),
    location: window.location.href,
    title: document.title,
    viewport: {
      innerWidth: window.innerWidth,
      innerHeight: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      devicePixelRatio: window.devicePixelRatio,
    },
    documents: [
      collectDocumentSnapshot(document, { kind: 'top-document' }),
      ...iframeSnapshots,
    ],
  };
}

export async function uploadDebugLogs(description?: string, entryPoint?: string): Promise<void> {
  const capturedLogs = getDebugLogs();
  const logs = prependReportMetadata(capturedLogs, description);
  const payload: BugReportUploadPayload = {
    version: appDisplayVersion,
    entryPoint: entryPoint?.trim() || undefined,
    description: description?.trim() || undefined,
    logs,
    domSnapshot: captureRenderedDomSnapshot(),
  };

  const upload = (url: string, credentials?: RequestCredentials) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials,
    body: JSON.stringify(payload),
  });

  let res = await upload('/api/upload_log');
  if (res.status === 401 && entryPoint && PUBLIC_FALLBACK_ENTRY_POINTS.has(entryPoint)) {
    res = await upload('/api/bootstrap_upload_log', 'omit');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Upload failed (${res.status}): ${text}`);
  }

  clearDebugLogs();
}
